<template>
  <div>
    <!-- 根元素下的内容 -->
    <div class="container">
      <div ref="threeCanvas" class="three-canvas"></div>
      <div class="info-box" v-if="selectedObjectInfo">
        {{ selectedObjectInfo }}
        <div v-if="selectedObject">
          <label>ID: <input v-model="selectedObject.userData.id" /></label><br>
          <label>Type: <input v-model="selectedObject.userData.type" /></label><br>
          <label>Strain: <input v-model="selectedObject.userData.strain" /></label><br>
          <button @click="updateInfo">Update Info</button>
        </div>
      </div>
    </div>

    <!-- 将 vs-card 包裹在同一个根元素内 -->
    <vs-card type="3">
      <template #title>
        <h3>Pot with a plant</h3>
      </template>
      <template #img>
        <img :src="require('@/assets/BH.jpg')" alt="">
      </template>
      <template #text>
        <p>
          Lorem ipsum dolor sit amet consectetur, adipisicing elit.
        </p>
      </template>
    </vs-card>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as dat from 'dat.gui';
import { modelLoader } from '@/utils/ModelLoader';

export default {
  name: 'ThreeJSComponent',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      lod: null,
      raycaster: new THREE.Raycaster(),
      mouse: new THREE.Vector2(),
      controls: null,
      initialPosition: new THREE.Vector3(),
      initialRotation: new THREE.Quaternion(),
      selectedObject: null,
      selectedObjectInfo: '',
      lastTime: performance.now(),
      frameCount: 0,
      gui: null,
      fpsController: null,
      fps: 0, // 用于显示的FPS变量
      frameTimes: [], // 用于存储帧时间间隔
      boundingSphere: null, // 包围球
    };
  },
  methods: {
    initThree() {
      // 初始化场景
      this.scene = new THREE.Scene();

      // 设置渲染器和画布
      this.renderer = new THREE.WebGLRenderer();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.setClearColor(0xD2B48C); // 浅棕色背景
      this.$refs.threeCanvas.appendChild(this.renderer.domElement);

      // 初始化相机
      this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 10000);
      this.camera.position.set(0, 0, 1000);

      // 初始化轨道控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.25;
      this.controls.enableZoom = true;

      // 添加环境光和方向光
      const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
      this.scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(5, 10, 7.5);
      this.scene.add(directionalLight);

      // 初始化 dat.GUI 控件
      this.gui = new dat.GUI();
      this.fpsController = this.gui.add(this, 'fps').name('FPS').listen();

      // 加载 LOD 模型
      this.loadLODModel();

      // 开始渲染循环
      this.animate();
    },
    async loadLODModel() {
      // 设置模型缩放比例和位置
      const modelScale = 0.3;
      const modelPosition = new THREE.Vector3(700, -985, 50);
      this.lod = new THREE.LOD();

      // 使用预加载的低分辨率模型
      const lowResModel = modelLoader.getModel('BH_low');
      if (lowResModel) {
        lowResModel.scene.scale.set(modelScale, modelScale, modelScale);
        lowResModel.scene.position.copy(modelPosition);
        this.lod.addLevel(lowResModel.scene, 500);
      }

      // 使用预加载的中分辨率模型
      const mediumResModel = modelLoader.getModel('BH_medium');
      if (mediumResModel) {
        mediumResModel.scene.scale.set(modelScale, modelScale, modelScale);
        mediumResModel.scene.position.copy(modelPosition);
        this.lod.addLevel(mediumResModel.scene, 300);
      }

      // 使用预加载的高分辨率模型
      const highResModel = modelLoader.getModel('BH_high');
      if (highResModel) {
        highResModel.scene.scale.set(modelScale, modelScale, modelScale);
        highResModel.scene.position.copy(modelPosition);
        this.lod.addLevel(highResModel.scene, 0);
      }

      // 将 LOD 对象添加到场景
      this.scene.add(this.lod);

      // 计算模型的包围球（以最高分辨率模型为准）
      if (highResModel) {
        const boundingBox = new THREE.Box3().setFromObject(highResModel.scene);
        this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
      }
    },
    animate() {
      requestAnimationFrame(this.animate.bind(this));

      // 记录每帧的时间间隔并计算平均FPS
      const now = performance.now();
      if (this.frameTimes.length > 60) this.frameTimes.shift(); // 仅保留最近的60帧
      this.frameTimes.push(now);

      if (this.frameTimes.length > 1) {
        const frameIntervals = this.frameTimes
          .slice(1)
          .map((time, i) => time - this.frameTimes[i]);
        const averageInterval = frameIntervals.reduce((sum, interval) => sum + interval, 0) / frameIntervals.length;
        this.fps = Math.round(1000 / averageInterval);
      }

      // 更新 LOD 距离
      if (this.boundingSphere) {
        const distance = this.camera.position.distanceTo(this.boundingSphere.center);
        this.updateLOD(distance);
      }

      this.controls.update();
      this.renderer.render(this.scene, this.camera); // 使用 renderer 渲染
    },
    resetModel() {
      if (this.model) {
        this.model.position.copy(this.initialPosition);
        this.model.quaternion.copy(this.initialRotation);
        this.selectedObjectInfo = '';
        if (this.selectedObject && this.selectedObject.material) {
          this.selectedObject.material.emissive.setHex(this.selectedObject.currentHex);
          this.selectedObject = null;
        }
      }
    },
    updateLOD(distance) {
      if (!this.lod || !this.lod.levels) return;

  const levels = this.lod.levels;

  

  if (distance > 500) {
    levels[0]?.object && (levels[0].object.visible = true);
    levels[1]?.object && (levels[1].object.visible = false);
    levels[2]?.object && (levels[2].object.visible = false);
  } else if (distance > 300) {
    levels[0]?.object && (levels[0].object.visible = false);
    levels[1]?.object && (levels[1].object.visible = true);
    levels[2]?.object && (levels[2].object.visible = false);
  } else {
    levels[0]?.object && (levels[0].object.visible = false);
    levels[1]?.object && (levels[1].object.visible = false);
    levels[2]?.object && (levels[2].object.visible = true);
  }
    },
    onWindowResize() {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.$refs.threeCanvas.removeEventListener('click', this.onMouseClick);
  },
  mounted() {
    this.initThree();

    window.addEventListener('resize', this.onWindowResize, false);
    this.$refs.threeCanvas.addEventListener('click', this.onMouseClick, false);
  },
};
</script>

<style scoped>
.container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.three-canvas {
  width: 100%;
  height: 100%;
}

::v-deep .vs-card {
  padding: 20px;
}

.button-container {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
}

.reset-button, .bend-button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  background: linear-gradient(45deg, #6ab7ff, #4f83cc);
  color: white;
  border: none;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.reset-button:hover, .bend-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
}

.info-box {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #333;
}
</style>
